import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../services/api';

const DEFAULT_PAGE_SIZE = 20;
const SERVER_PAGE_SIZE = 100; // backend uses PAGE_SIZE = 100

const HIDDEN_COLUMNS = new Set(['ema50', 'ema200', 'adx', 'atr']);
const COLUMN_LABELS = {
  tp2Percent: 'tp2 %',
  tp1Percent: 'tp1 %',
  tp1Price: 'tp1',
  tp2Price: 'tp2',
  exitReason: 'Exit',
  totalUsdt: 'Total USDT',
  pnlUsdt: 'PnL (USDT)',
};

const reorderColumns = (cols) => {
  const out = [...cols];
  const pnlKey = 'pnlUsdt';
  const exitKey = 'exitReason';
  const entryKey = 'entryPrice';

  if (!out.includes(pnlKey) || !out.includes(exitKey) || !out.includes(entryKey)) {
    return out;
  }

  const pnlIdx = out.indexOf(pnlKey);
  out.splice(pnlIdx, 1);
  const exitIdx = out.indexOf(exitKey);
  out.splice(exitIdx + 1, 0, pnlKey);
  return out;
};

const PaginatedTable = ({ rows, formatCellValue, pageSize = DEFAULT_PAGE_SIZE }) => {
  const [page, setPage] = useState(1);
  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const totalRows = safeRows.length;

  const columns = useMemo(() => {
    if (safeRows.length === 0) return [];
    const allColumns = Object.keys(safeRows[0] || {});
    const filtered = allColumns.filter((c) => !HIDDEN_COLUMNS.has(c));
    return reorderColumns(filtered);
  }, [safeRows]);

  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));

  useEffect(() => {
    setPage(1);
  }, [totalRows, pageSize]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
    if (page < 1) setPage(1);
  }, [page, pageCount]);

  if (columns.length === 0) return null;
  if (totalRows === 0) return null;

  const startIdx = (page - 1) * pageSize;
  const endIdx = Math.min(totalRows, startIdx + pageSize);
  const pageRows = safeRows.slice(startIdx, endIdx);

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="bg-dark-700">
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white whitespace-nowrap"
                >
                  {COLUMN_LABELS[col] || col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, idx) => (
              <tr key={startIdx + idx} className="border-b border-dark-700 hover:bg-dark-700/30">
                {columns.map((col) => {
                  const value = row[col];
                  const isTrendCol = col.toLowerCase() === 'trend';
                  const isSideCol = col === 'longOrShort' || col === 'buyType';

                  if (isTrendCol || isSideCol) {
                    const formattedValue = formatCellValue(col, value);
                    const normalized = String(formattedValue ?? '').toUpperCase();
                    let badgeClass = 'bg-dark-700 text-dark-200';
                    if (normalized === 'UP' || normalized === 'LONG') badgeClass = 'bg-green-500 text-white';
                    else if (normalized === 'DOWN' || normalized === 'SHORT') badgeClass = 'bg-red-500 text-white';
                    return (
                      <td key={col} className="px-4 py-3 text-xs sm:text-sm text-dark-200">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badgeClass}`}>
                          {normalized || '-'}
                        </span>
                      </td>
                    );
                  }

                  if (col.toLowerCase().includes('pnl') || col === 'netProfit' || col === 'maxDrawdown') {
                    const n = Number(value);
                    const colorClass = Number.isNaN(n)
                      ? 'text-dark-200'
                      : n >= 0
                      ? 'text-green-400'
                      : 'text-red-400';
                    return (
                      <td key={col} className="px-4 py-3 text-xs sm:text-sm">
                        <span className={colorClass}>{formatCellValue(col, value)}</span>
                      </td>
                    );
                  }

                  return (
                    <td key={col} className="px-4 py-3 text-xs sm:text-sm text-dark-200 whitespace-nowrap">
                      {formatCellValue(col, value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="text-xs sm:text-sm text-dark-400">
            Showing {startIdx + 1}-{endIdx} of {totalRows}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1 rounded-md text-xs sm:text-sm border border-dark-600 bg-dark-700 text-dark-200 hover:bg-dark-600 disabled:opacity-50"
            >
              Prev
            </button>
            <div className="text-xs sm:text-sm text-dark-300">
              Page <span className="text-white font-semibold">{page}</span> / {pageCount}
            </div>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={page >= pageCount}
              className="px-3 py-1 rounded-md text-xs sm:text-sm border border-dark-600 bg-dark-700 text-dark-200 hover:bg-dark-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Simulator10s = () => {
  const [activeTab, setActiveTab] = useState('positions'); // symbols | trendingCrud | positions | history | currentLoss | drawdown | tradeLogs
  const [symbols, setSymbols] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [serverPage, setServerPage] = useState(0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trendingForm, setTrendingForm] = useState({
    symbol: '',
    startTime: '',
    endTime: '',
    upOrDown: '',
    rank: 0,
    volatilityScore: 0,
    strategyId: 0,
    period: 0,
    multiplier: 0,
    stopLoss: 0,
    tp1Per: 0,
    tp2Per: 0,
  });

  const tabs = [
    { id: 'positions', label: 'Positions' },
    { id: 'history', label: 'Trade History' },
    { id: 'currentLoss', label: 'Current Loss' },
    { id: 'drawdown', label: 'Max Drawdown' },
    { id: 'tradeLogs', label: 'Trade Logs' },
    { id: 'symbols', label: 'Symbols' },
    { id: 'trendingCrud', label: 'Trending CRUD' },
  ];

  const formatCellValue = (col, value) => {
    if (value == null) return '-';

    const lowerCol = col.toLowerCase();
    if (
      lowerCol.includes('price') ||
      lowerCol.includes('pnl') ||
      lowerCol.includes('percent') ||
      lowerCol.includes('usdt') ||
      lowerCol === 'marginused' ||
      col === 'volatilityScore' ||
      col === 'maxDrawdown' ||
      col === 'netProfit' ||
      col === 'totalTrades' ||
      col === 'quantity' ||
      col === 'positionAmt'
    ) {
      const n = Number(value);
      if (!Number.isNaN(n)) {
        if (lowerCol === 'marginused') return n.toFixed(2);
        return n.toFixed(5);
      }
    }

    if (
      col === 'runTime' ||
      col === 'entryTime' ||
      col === 'exitTime' ||
      col === 'time' ||
      col === 'openTime' ||
      col === 'closeTime' ||
      col === 'tradeTime' ||
      col === 'startTime' ||
      col === 'endTime'
    ) {
      const s = String(value);
      if (s.includes('T')) return s.replace('T', ' ').split('.')[0];
      const n = Number(s);
      if (!Number.isNaN(n) && s.length > 10) {
        return new Date(n).toISOString().replace('T', ' ').split('.')[0];
      }
      return s;
    }

    if (col === 'longOrShort' || col === 'buyType') {
      const n = Number(value);
      if (n === 1) return 'LONG';
      if (n === 2) return 'SHORT';
      return String(value);
    }

    return String(value);
  };

  const hasNextServerPage = useMemo(() => {
    // drawdown report returns a single row; treat as no paging
    if (activeTab === 'drawdown') return false;
    if (activeTab === 'symbols') return Array.isArray(symbols) && symbols.length >= SERVER_PAGE_SIZE;
    if (activeTab === 'trendingCrud') return Array.isArray(data) && data.length >= SERVER_PAGE_SIZE;
    return Array.isArray(data) && data.length >= SERVER_PAGE_SIZE;
  }, [activeTab, data, symbols]);

  const loadSymbols = async (page = 0) => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getSimulated10sSymbols({ page });
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setSymbols(list);
      if (!selectedSymbol && list.length > 0 && list[0]?.symbol) {
        setSelectedSymbol(String(list[0].symbol).toUpperCase());
      }
    } catch (e) {
      const msg = String(e?.message || '');
      setSymbols([]);
      setError(msg.includes('status: 404') ? 'No symbols found.' : 'Failed to load symbols.');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async ({ tabId, symbol, page }) => {
    setLoading(true);
    setError('');
    setData([]);
    try {
      let res;
      switch (tabId) {
        case 'symbols':
          await loadSymbols(page);
          setData([]);
          return;
        case 'trendingCrud':
          res = await apiService.getCurrentTrending10sList({ page });
          break;
        case 'positions':
          res = await apiService.getSimulated10sPositions({ symbol, page });
          break;
        case 'history':
          res = await apiService.getSimulated10sHistory({ symbol, page });
          break;
        case 'tradeLogs':
          res = await apiService.getSimulated10sLogs({ symbol, page });
          break;
        case 'currentLoss':
          res = await apiService.getSimulated10sCurrentLoss({ symbol, page });
          break;
        case 'drawdown':
          res = await apiService.getSimulated10sDrawdown({ symbol, page });
          break;
        default:
          return;
      }

      const finalData = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setData(finalData);
    } catch (e) {
      const msg = String(e?.message || '');
      setData([]);
      setError(msg.includes('status: 404') ? 'No data found for this selection.' : 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshPositions = async () => {
    if (!selectedSymbol) {
      setError('Symbol is required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await apiService.refreshSimulated10sPositions({ symbol: selectedSymbol, page: serverPage });
      setData(Array.isArray(res) ? res : []);
    } catch (e) {
      const msg = String(e?.message || '');
      setData([]);
      setError(msg.includes('status: 404') ? 'No positions found for this symbol.' : 'Failed to refresh positions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSymbols(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setServerPage(0);
  }, [activeTab, selectedSymbol]);

  useEffect(() => {
    if (activeTab === 'symbols' || activeTab === 'trendingCrud') {
      fetchData({ tabId: activeTab, symbol: selectedSymbol, page: serverPage });
      return;
    }
    if (!selectedSymbol) return;
    fetchData({ tabId: activeTab, symbol: selectedSymbol, page: serverPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedSymbol, serverPage]);

  const setTrendingField = (name, value) => {
    const intFields = new Set(['rank', 'strategyId', 'period', 'multiplier']);
    const floatFields = new Set(['volatilityScore', 'stopLoss', 'tp1Per', 'tp2Per']);
    setTrendingForm((prev) => {
      let v = value;
      if (intFields.has(name)) v = value === '' ? '' : Number.parseInt(value, 10) || 0;
      if (floatFields.has(name)) v = value === '' ? '' : Number.parseFloat(value) || 0;
      return { ...prev, [name]: v };
    });
  };

  // Backend expects: yyyy-MM-dd HH:mm:ss.SSS (see spring.jackson.date-format)
  // datetime-local gives: yyyy-MM-ddTHH:mm (no timezone). Convert without timezone math.
  const toServerDateStringOrNull = (dtLocal) => {
    if (!dtLocal) return null;
    const s = String(dtLocal).trim();
    // Accept "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss"
    const m = s.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::(\d{2}))?$/);
    if (!m) return null;
    const date = m[1];
    const hm = m[2];
    const sec = m[3] || '00';
    return `${date} ${hm}:${sec}.000`;
  };

  const fromApiDateToLocal = (value) => {
    if (!value) return '';
    // server returns "YYYY-MM-DD HH:mm:ss.SSS"
    if (typeof value === 'string') {
      const s = value.trim();
      const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?::\d{2}(?:\.\d{1,3})?)?$/);
      if (m) return `${m[1]}T${m[2]}`;
      // fallback for ISO strings
      if (s.includes('T')) {
        const d = new Date(s);
        if (!Number.isNaN(d.getTime())) {
          const pad = (n) => String(n).padStart(2, '0');
          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        }
      }
      return '';
    }
    // if backend ever sends epoch millis
    if (typeof value === 'number') {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    return '';
  };

  const trendingPayload = () => ({
    symbol: String(trendingForm.symbol || '').trim().toUpperCase(),
    startTime: toServerDateStringOrNull(trendingForm.startTime),
    endTime: toServerDateStringOrNull(trendingForm.endTime),
    upOrDown: trendingForm.upOrDown || null,
    rank: Number(trendingForm.rank) || 0,
    volatilityScore: Number(trendingForm.volatilityScore) || 0,
    strategyId: Number(trendingForm.strategyId) || 0,
    period: Number(trendingForm.period) || 0,
    multiplier: Number(trendingForm.multiplier) || 0,
    stopLoss: Number(trendingForm.stopLoss) || 0,
    tp1Per: Number(trendingForm.tp1Per) || 0,
    tp2Per: Number(trendingForm.tp2Per) || 0,
  });

  const handleTrendingLoad = async () => {
    const sym = String(trendingForm.symbol || '').trim().toUpperCase();
    if (!sym) {
      setError('Symbol is required to load.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const row = await apiService.getCurrentTrending10sBySymbol(sym);
      setTrendingForm({
        symbol: row?.symbol || sym,
        startTime: fromApiDateToLocal(row?.startTime),
        endTime: fromApiDateToLocal(row?.endTime),
        upOrDown: row?.upOrDown || '',
        rank: row?.rank ?? 0,
        volatilityScore: row?.volatilityScore ?? 0,
        strategyId: row?.strategyId ?? 0,
        period: row?.period ?? 0,
        multiplier: row?.multiplier ?? 0,
        stopLoss: row?.stopLoss ?? 0,
        tp1Per: row?.tp1Per ?? 0,
        tp2Per: row?.tp2Per ?? 0,
      });
    } catch (e) {
      const msg = String(e?.message || '');
      setError(msg.includes('status: 404') ? `Symbol not found: ${sym}` : 'Failed to load symbol.');
    } finally {
      setLoading(false);
    }
  };

  const handleTrendingAdd = async () => {
    const body = trendingPayload();
    if (!body.symbol) {
      setError('Symbol is required to add.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await apiService.addCurrentTrending10s(body);
      await loadSymbols(0);
      await fetchData({ tabId: 'trendingCrud', symbol: selectedSymbol, page: serverPage });
    } catch (e) {
      const msg = String(e?.message || '');
      if (msg.includes('status: 409')) setError(`Already exists: ${body.symbol}`);
      else setError('Failed to add symbol.');
    } finally {
      setLoading(false);
    }
  };

  const handleTrendingUpdate = async () => {
    const body = trendingPayload();
    if (!body.symbol) {
      setError('Symbol is required to update.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await apiService.updateCurrentTrending10s(body.symbol, body);
      await loadSymbols(0);
      await fetchData({ tabId: 'trendingCrud', symbol: selectedSymbol, page: serverPage });
    } catch (e) {
      const msg = String(e?.message || '');
      setError(msg.includes('status: 404') ? `Symbol not found: ${body.symbol}` : 'Failed to update symbol.');
    } finally {
      setLoading(false);
    }
  };

  const handleTrendingRemove = async () => {
    const sym = String(trendingForm.symbol || '').trim().toUpperCase();
    if (!sym) {
      setError('Symbol is required to remove.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await apiService.removeCurrentTrending10s(sym);
      await loadSymbols(0);
      await fetchData({ tabId: 'trendingCrud', symbol: selectedSymbol, page: serverPage });
    } catch (e) {
      const msg = String(e?.message || '');
      setError(msg.includes('status: 404') ? `Symbol not found: ${sym}` : 'Failed to remove symbol.');
    } finally {
      setLoading(false);
    }
  };

  const totals = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return null;
    const pnlKey = data[0] && Object.prototype.hasOwnProperty.call(data[0], 'pnl') ? 'pnl' : null;
    const profitKey = !pnlKey && data[0] && Object.prototype.hasOwnProperty.call(data[0], 'netProfit') ? 'netProfit' : null;
    const key = pnlKey || profitKey;
    if (!key) return { count: data.length, total: null, key: null };
    const total = data.reduce((s, r) => s + (Number(r?.[key]) || 0), 0);
    return { count: data.length, total, key };
  }, [data]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl sm:text-2xl font-semibold text-white">Simulator 10s</h3>
          <p className="text-dark-400 text-sm mt-1">10-second simulator data (symbol-scoped)</p>
        </div>
      </div>

      <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
        <div className="flex gap-3 border-b border-dark-700 mb-4 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-3 py-2 rounded-t transition-colors whitespace-nowrap font-medium text-sm ${
                activeTab === t.id
                  ? 'bg-dark-700 text-white border-b-2 border-primary-500'
                  : 'text-dark-400 hover:text-white hover:bg-dark-700/50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label className="text-dark-300 text-xs sm:text-sm font-medium mb-1">Symbol *</label>
            <div className="flex gap-2">
              <select
                value={selectedSymbol}
                onChange={(e) => setSelectedSymbol(e.target.value.toUpperCase())}
                className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500 min-w-[220px]"
              >
                <option value="">Select symbol...</option>
                {symbols.map((s) => (
                  <option key={`${s.symbol}-${s.rank ?? ''}`} value={String(s.symbol || '').toUpperCase()}>
                    {String(s.symbol || '').toUpperCase()}
                    {s.rank != null ? ` (rank ${s.rank})` : ''}
                  </option>
                ))}
              </select>
              <input
                value={selectedSymbol}
                onChange={(e) => setSelectedSymbol(e.target.value.toUpperCase())}
                placeholder="Or type symbol (e.g. BTCUSDT)"
                className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500 min-w-[220px]"
              />
              <button
                type="button"
                onClick={() => loadSymbols(0)}
                disabled={loading}
                className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600 disabled:opacity-50"
              >
                Reload Symbols
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-dark-300 text-xs sm:text-sm font-medium">Server page</label>
            <button
              type="button"
              onClick={() => setServerPage((p) => Math.max(0, p - 1))}
              disabled={loading || serverPage <= 0}
              className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600 disabled:opacity-50"
            >
              Prev
            </button>
            <div className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-sm text-white">
              {serverPage}
            </div>
            <button
              type="button"
              onClick={() => setServerPage((p) => p + 1)}
              disabled={loading || !hasNextServerPage}
              className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600 disabled:opacity-50"
              title={!hasNextServerPage ? 'No more pages (last page likely < 100 rows)' : 'Next page'}
            >
              Next
            </button>
          </div>

          {activeTab === 'positions' && (
            <button
              type="button"
              onClick={handleRefreshPositions}
              disabled={loading || !selectedSymbol}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Refreshing...' : 'Refresh Positions'}
            </button>
          )}
        </div>

        {error && (
          <div className="p-4 mb-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-300 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin mb-4"></div>
            <p className="text-dark-400 text-sm italic">Loading...</p>
          </div>
        ) : activeTab === 'symbols' ? (
          symbols.length === 0 ? (
            <div className="p-12 text-center text-dark-500 italic bg-dark-900/30 rounded-xl border border-dashed border-dark-700">
              No symbols found
            </div>
          ) : (
            <PaginatedTable rows={symbols} formatCellValue={formatCellValue} pageSize={20} />
          )
        ) : activeTab === 'trendingCrud' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              {data.length === 0 ? (
                <div className="p-8 text-center text-dark-500 italic bg-dark-900/30 rounded-xl border border-dashed border-dark-700">
                  No rows on this page
                </div>
              ) : (
                <PaginatedTable rows={data} formatCellValue={formatCellValue} pageSize={20} />
              )}
            </div>
            <div className="bg-dark-900/30 border border-dark-700 rounded-xl p-4">
              <div className="text-white font-semibold mb-3">Current Trending 10s</div>

              <div className="space-y-3">
                <div>
                  <label className="block text-dark-300 text-xs sm:text-sm font-medium mb-1">Symbol *</label>
                  <input
                    value={trendingForm.symbol}
                    onChange={(e) => setTrendingField('symbol', e.target.value)}
                    placeholder="e.g. BTCUSDT"
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-dark-300 text-xs sm:text-sm font-medium mb-1">Rank</label>
                    <input
                      type="number"
                      value={trendingForm.rank}
                      onChange={(e) => setTrendingField('rank', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-dark-300 text-xs sm:text-sm font-medium mb-1">Up/Down</label>
                    <select
                      value={trendingForm.upOrDown}
                      onChange={(e) => setTrendingField('upOrDown', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    >
                      <option value="">(blank)</option>
                      <option value="UP">UP</option>
                      <option value="DOWN">DOWN</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-dark-300 text-xs sm:text-sm font-medium mb-1">Strategy ID</label>
                    <input
                      type="number"
                      value={trendingForm.strategyId}
                      onChange={(e) => setTrendingField('strategyId', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-dark-300 text-xs sm:text-sm font-medium mb-1">Volatility Score</label>
                    <input
                      type="number"
                      step="0.00001"
                      value={trendingForm.volatilityScore}
                      onChange={(e) => setTrendingField('volatilityScore', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-dark-300 text-xs sm:text-sm font-medium mb-1">Period</label>
                    <input
                      type="number"
                      value={trendingForm.period}
                      onChange={(e) => setTrendingField('period', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-dark-300 text-xs sm:text-sm font-medium mb-1">Multiplier</label>
                    <input
                      type="number"
                      value={trendingForm.multiplier}
                      onChange={(e) => setTrendingField('multiplier', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-dark-300 text-xs sm:text-sm font-medium mb-1">StopLoss</label>
                    <input
                      type="number"
                      step="0.00001"
                      value={trendingForm.stopLoss}
                      onChange={(e) => setTrendingField('stopLoss', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-dark-300 text-xs sm:text-sm font-medium mb-1">TP1 %</label>
                    <input
                      type="number"
                      step="0.00001"
                      value={trendingForm.tp1Per}
                      onChange={(e) => setTrendingField('tp1Per', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-dark-300 text-xs sm:text-sm font-medium mb-1">TP2 %</label>
                    <input
                      type="number"
                      step="0.00001"
                      value={trendingForm.tp2Per}
                      onChange={(e) => setTrendingField('tp2Per', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-dark-300 text-xs sm:text-sm font-medium mb-1">Start Time</label>
                    <input
                      type="datetime-local"
                      value={trendingForm.startTime}
                      onChange={(e) => setTrendingField('startTime', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-dark-300 text-xs sm:text-sm font-medium mb-1">End Time</label>
                    <input
                      type="datetime-local"
                      value={trendingForm.endTime}
                      onChange={(e) => setTrendingField('endTime', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                  <button
                    type="button"
                    onClick={handleTrendingLoad}
                    disabled={loading}
                    className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600 disabled:opacity-50"
                  >
                    Load
                  </button>
                  <button
                    type="button"
                    onClick={handleTrendingAdd}
                    disabled={loading}
                    className="px-3 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={handleTrendingUpdate}
                    disabled={loading}
                    className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                  >
                    Update
                  </button>
                  <button
                    type="button"
                    onClick={handleTrendingRemove}
                    disabled={loading}
                    className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : !selectedSymbol ? (
          <div className="p-12 text-center text-dark-500 italic bg-dark-900/30 rounded-xl border border-dashed border-dark-700">
            Select a symbol to load data
          </div>
        ) : data.length === 0 ? (
          <div className="p-12 text-center text-dark-500 italic bg-dark-900/30 rounded-xl border border-dashed border-dark-700">
            No data found for the current selection
          </div>
        ) : (
          <>
            {totals && (
              <div className="mb-4 inline-flex flex-wrap items-center gap-4 px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg shadow-md">
                <span className="text-dark-400 text-sm font-medium">
                  Count: <span className="text-white">{totals.count}</span>
                </span>
                {totals.total != null && (
                  <span className="text-dark-400 text-sm font-medium">
                    Total {totals.key}:{' '}
                    <span className={`text-lg font-bold ${totals.total >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {totals.total.toFixed(5)}
                    </span>
                  </span>
                )}
              </div>
            )}
            <PaginatedTable rows={data} formatCellValue={formatCellValue} pageSize={20} />
          </>
        )}
      </div>
    </div>
  );
};

export default Simulator10s;

